import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Lock } from "lucide-react";
import { errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo, useBranding } from "@/components/Brand";
import { AddressMap } from "@/components/AddressMap";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [id, setId] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const brand = useBranding();
  if (user) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(id, pw);
      nav(u.role === "mecanicien" ? "/atelier" : "/");
    } catch (ex) {
      setErr(errMsg(ex));
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen bg-[#050505] text-white lg:grid-cols-[1.15fr_1fr]">
      <div className="relative hidden overflow-hidden border-r border-white/10 lg:block">
        <img src="/brand/login-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40 grayscale" />
        <div className="absolute inset-0 bg-[#050505]/60" />
        <div className="grid-bg absolute inset-0 opacity-30" />
        <div className="relative flex h-full flex-col justify-between p-14">
          <div className="eyebrow text-white/60">Alger · Atelier mécanique</div>
          <div className="fade-up">
            <Logo variant="premium" custom={false} className="w-[460px] max-w-full" />
            <p className="mt-8 max-w-md text-lg text-white/70">Réception, devis, atelier, facturation. Un seul outil pour suivre chaque véhicule, du rendez-vous à la restitution.</p>
          </div>
          <div className="max-w-md space-y-3 text-sm text-white/60" data-testid="login-contact">
            <AddressMap address={brand?.address} />
            <div>{brand?.address}</div>
            <div className="font-mono-data">{brand?.phone} · {brand?.email}</div>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="mb-10 lg:hidden"><Logo variant="premium" custom={false} className="w-72" /></div>
          <img src="/brand/nms-monogram.svg" alt="NMS" className="mb-8 hidden h-10 lg:block" />
          <h1 className="text-4xl font-bold uppercase tracking-tight">Connexion</h1>
          <p className="mt-2 text-sm text-white/60">Accès réservé au personnel de NMS.</p>
          <div className="mt-10 space-y-5">
            <label className="block">
              <span className="eyebrow text-white/60">Identifiant</span>
              <Input value={id} onChange={(e) => setId(e.target.value)} autoComplete="username" className="mt-2 h-12 border-white/15 bg-[#121212] text-white" data-testid="login-identifier-input" />
            </label>
            <label className="block">
              <span className="eyebrow text-white/60">Mot de passe</span>
              <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" className="mt-2 h-12 border-white/15 bg-[#121212] text-white" data-testid="login-password-input" toggleTestId="login-password-toggle" />
            </label>
            {err && <div className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="login-error">{err}</div>}
            <button disabled={busy || !id || !pw} className="group flex h-14 w-full items-center justify-between rounded-sm bg-[#E63946] px-6 font-display text-base font-bold uppercase tracking-wider transition-colors hover:bg-[#D62828] disabled:opacity-50" data-testid="login-submit-btn">
              Se connecter
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
            </button>
          </div>
          <div className="mt-10 flex items-center gap-2 text-xs text-white/40"><Lock className="h-3.5 w-3.5" />Déconnexion automatique après inactivité.</div>
          <div className="mt-8 space-y-3 text-sm text-white/60 lg:hidden" data-testid="login-contact-mobile">
            <AddressMap address={brand?.address} />
            <div>{brand?.address}</div>
            <div className="font-mono-data">{brand?.phone} · {brand?.email}</div>
          </div>
        </form>
      </div>
    </div>
  );
}
